import { Area, Bar, ComposedChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";

const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

const STAGES = [
  { key: "views", label: "Landing views", rate: null },
  { key: "vsl_plays", label: "VSL plays", rate: (t) => (t.views ? Math.round((t.vsl_plays * 1000) / t.views) / 10 : null), rateLabel: "of views" },
  { key: "form_starts", label: "Form starts", rate: (t) => t.rates.view_to_start, rateLabel: "of views" },
  { key: "photo_uploads", label: "Photo uploads", rate: (t) => t.rates.start_to_photo, rateLabel: "of starts" },
  { key: "leads", label: "Leads", rate: (t) => t.rates.photo_to_lead, rateLabel: "of uploads" },
];

export const StatCards = ({ totals }) => (
  <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="funnel-stat-cards">
    {STAGES.map((s, i) => (
      <div key={s.key} className={`p-5 border ${i === STAGES.length - 1 ? "col-span-2 lg:col-span-1 bg-black text-white border-black" : "bg-white border-gray-200"}`} data-testid={`stat-${s.key}`}>
        <p className={`text-[10px] uppercase tracking-[0.2em] ${i === STAGES.length - 1 ? "text-[#c19a6b]" : "text-gray-500"}`}>{s.label}</p>
        <p className="font-display text-3xl sm:text-4xl mt-2" data-testid={`stat-${s.key}-value`}>{totals[s.key]}</p>
        {s.rate && (
          <p className={`text-xs mt-1.5 ${i === STAGES.length - 1 ? "text-white/60" : "text-gray-500"}`}>
            <span className="font-semibold">{fmtPct(s.rate(totals))}</span> {s.rateLabel}
          </p>
        )}
      </div>
    ))}
  </div>
);

export const FunnelBars = ({ totals }) => {
  const rows = [
    ["Landing views", totals.views, "bg-gray-300"],
    ["Form starts", totals.form_starts, "bg-gray-500"],
    ["Photo uploads", totals.photo_uploads, "bg-[#c19a6b]"],
    ["Leads", totals.leads, "bg-black"],
  ];
  const max = Math.max(1, ...rows.map((r) => r[1]));
  return (
    <div className="bg-white border border-gray-200 p-6" data-testid="funnel-bars">
      <div className="flex items-baseline justify-between mb-5">
        <p className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Conversion funnel</p>
        <p className="text-xs text-gray-500">View → lead <span className="font-semibold text-black" data-testid="view-to-lead-rate">{fmtPct(totals.rates.view_to_lead)}</span></p>
      </div>
      <div className="space-y-3">
        {rows.map(([label, n, color]) => (
          <div key={label} className="grid grid-cols-[120px_1fr_48px] items-center gap-3 text-sm">
            <span className="text-gray-600">{label}</span>
            <div className="h-6 bg-gray-100">
              <div className={`h-full ${color} transition-[width] duration-500`} style={{ width: `${Math.max((n / max) * 100, n ? 1.5 : 0)}%` }} />
            </div>
            <span className="font-semibold text-right">{n}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const DailyChart = ({ byDay }) => {
  const data = byDay.map((d) => ({ ...d, label: d.day.slice(5).replace("-", "/") }));
  return (
    <div className="bg-white border border-gray-200 p-6 overflow-hidden" data-testid="daily-chart">
      <p className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-5">Per day (Toronto time)</p>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <CartesianGrid stroke="#eee" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#888" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#888" }} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 0, border: "1px solid #ddd" }} labelFormatter={(l, p) => p?.[0]?.payload?.day || l} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="square" />
            <Area type="monotone" dataKey="views" name="Landing views" stroke="#999" fill="#eee" strokeWidth={1.5} />
            <Bar dataKey="form_starts" name="Form starts" fill="#c19a6b" maxBarSize={18} />
            <Bar dataKey="leads" name="Leads" fill="#000" maxBarSize={18} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const CampaignTable = ({ campaigns }) => (
  <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="campaign-table">
    <div className="px-6 pt-6 pb-3 flex items-baseline justify-between">
      <p className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Per source / campaign</p>
      <p className="text-xs text-gray-400">From utm_source + utm_campaign on the ad link</p>
    </div>
    <table className="w-full text-sm">
      <thead>
        <tr className="border-y border-gray-200 text-left text-[11px] uppercase tracking-[0.12em] text-gray-500">
          {["Source", "Campaign", "Views", "VSL plays", "Form starts", "Photo uploads", "Leads", "View → lead"].map((h, i) => (
            <th key={h} className={`px-4 py-3 font-medium whitespace-nowrap ${i >= 2 ? "text-right" : ""}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {campaigns.length === 0 && (
          <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400" data-testid="no-campaign-data">No traffic yet in this period. Add <code>?utm_source=facebook&amp;utm_campaign=…</code> to your ad links to see them broken out here.</td></tr>
        )}
        {campaigns.map((c) => (
          <tr key={`${c.utm_source}|${c.utm_campaign}`} className="border-b border-gray-100" data-testid={`campaign-row-${c.utm_source}-${c.utm_campaign || "none"}`}>
            <td className="px-4 py-3 font-medium">{c.utm_source}</td>
            <td className="px-4 py-3 text-gray-600">{c.utm_campaign || <span className="text-gray-300">—</span>}</td>
            <td className="px-4 py-3 text-right">{c.views}</td>
            <td className="px-4 py-3 text-right">{c.vsl_plays}</td>
            <td className="px-4 py-3 text-right">{c.form_starts}</td>
            <td className="px-4 py-3 text-right">{c.photo_uploads}</td>
            <td className="px-4 py-3 text-right font-semibold">{c.leads}</td>
            <td className="px-4 py-3 text-right text-[#8a6a42] font-semibold">{fmtPct(c.rates.view_to_lead)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const DailyTable = ({ byDay }) => {
  const rows = [...byDay].reverse().filter((d) => d.views || d.form_starts || d.photo_uploads || d.leads);
  return (
    <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="daily-table">
      <p className="px-6 pt-6 pb-3 text-[11px] uppercase tracking-[0.18em] text-gray-500">Days with activity</p>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-y border-gray-200 text-left text-[11px] uppercase tracking-[0.12em] text-gray-500">
            {["Day", "Views", "VSL plays", "Form starts", "Photo uploads", "Leads", "View → lead"].map((h, i) => (
              <th key={h} className={`px-4 py-3 font-medium whitespace-nowrap ${i >= 1 ? "text-right" : ""}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">No activity yet.</td></tr>}
          {rows.map((d) => (
            <tr key={d.day} className="border-b border-gray-100" data-testid={`day-row-${d.day}`}>
              <td className="px-4 py-3 font-medium whitespace-nowrap">{d.day}</td>
              <td className="px-4 py-3 text-right">{d.views}</td>
              <td className="px-4 py-3 text-right">{d.vsl_plays}</td>
              <td className="px-4 py-3 text-right">{d.form_starts}</td>
              <td className="px-4 py-3 text-right">{d.photo_uploads}</td>
              <td className="px-4 py-3 text-right font-semibold">{d.leads}</td>
              <td className="px-4 py-3 text-right text-[#8a6a42]">{fmtPct(d.rates.view_to_lead)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
